/* eslint-disable camelcase */
import React, {
  useContext, useEffect, useMemo
} from 'react'
import cookie from 'react-cookies'
import { Route, Switch, useLocation } from 'react-router-dom'
import ConsumerActivationPaths from '@sites/consumer/activation/ConsumerActivationPaths'
import { ErrorBoundary } from 'react-error-boundary'

import NewPhoneNumberPage from '@sites/shared/PhoneNumberPage'
import PlansPage from '@sites/shared/PlansPage'

import AccountSetupPage from '@sites/shared/AccountSetupPage'
import PaymentPage from '@sites/consumer/activation/PaymentPage'
import IntroPage from '@sites/consumer/activation/IntroPage'
import TransactionSummaryPage from '@sites/shared/TransactionSummaryPage'
import SimSelectionPage from '@sites/shared/SimSelectionPage'
import PlanCardFooter from '@sites/consumer/activation/PlanCardFooter'
import Sites from '@sites/index'

import SiteHeader from '@apps/components/SiteHeader'
import { ApplicationContext, APPLICATION_STATE } from '@apps/contexts/ApplicationContext'
import ErrorSection from '@apps/flows/activation/sections/ErrorSection'

import { useDispatch, useSelector } from 'react-redux'
import { setUserType, recordUserInfo } from '@apps/redux/features/CommonSlice'
import { updateAccountSetupState } from '@apps/redux/features/AccountSetupSlice'
import { PLANS_STATE } from '@apps/redux/features/PlansSlice'
import AlreadyActivated from '@sites/consumer/activation/AlreadyActivated'
import VerifyPage from '@sites/consumer/activation/VerifyPage'
import ProcessLoader from '@sites/consumer/activation/ProcessLoader'
import ProcessFailure from '@sites/consumer/activation/ProcessFailure'
import Loader, { SIZE } from '@apps/components/Loader'

const ActivationMount = () => {
  const dispatch = useDispatch()
  const {
    [PLANS_STATE.PRE_SELECTED_PLAN]: preSelectedPlan,
    [PLANS_STATE.PLAN]: currentPlan
  } = useSelector((state) => state.plansPage)

  const consumerActivationPaths = new ConsumerActivationPaths()
  const location = useLocation()
  const isSplashPath = location.pathname === consumerActivationPaths.Splash
  const {
    [APPLICATION_STATE.PROVINCES]: mappedProvinceList,
    [APPLICATION_STATE.CURRENT_LANGUAGE]: currentLanguage,
    [APPLICATION_STATE.IS_LANDING_LOGIC_NOT_COMPLETE]: is_landing_logic_not_complete,
    [APPLICATION_STATE.IS_ACTIVATE_ALL_TRIGERRED]: is_activate_all_trigerred
  } = useContext(ApplicationContext)

  const provinces = useMemo(() => {
    return mappedProvinceList?.map((item) => {
      const { label, value, shortName } = item
      return {
        value,
        name: label[currentLanguage],
        shortName
      }
    })
  }, [mappedProvinceList])

  const provinceCookie = cookie.load('prov')
  useEffect(() => {
    if (provinces && provinceCookie) {
      const province = provinces?.find(
        (prov) => prov.shortName?.toLowerCase() === provinceCookie.toLowerCase()
      )
      if (province) {
        dispatch(updateAccountSetupState({ province: province.value }))
      }
    }
  }, [provinces, provinceCookie])

  // Entry URL, locked after the first dispatch (CommonSlice.recordUserInfo).
  // `setCreateQuote` derives the quote's `isPlanSelected` from it, which drives
  // the 4- vs 5-step tracker across a refresh. Must be captured here: before the
  // landing logic creates the quote, and on any entry URL.
  useEffect(() => {
    dispatch(recordUserInfo({ referrerUrl: location.pathname }))
  }, [])

  useEffect(() => {
    const currentPath = window.location.pathname
    if (
      currentPath !== consumerActivationPaths.Base &&
      currentPath !== consumerActivationPaths.Splash &&
      !currentPath.includes('/plans/')
    ) {
      window.location = consumerActivationPaths.Base
    }
    dispatch(setUserType(Sites.consumer))
  }, [])

  return (
    <>
      <SiteHeader />
      <ErrorBoundary fallback={<ErrorSection />}>
        {!isSplashPath && is_activate_all_trigerred && (
          <div className='fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-100 overflow-hidden bg-white flex flex-col items-center justify-center opacity-50' />
        )}

        {!isSplashPath && is_landing_logic_not_complete && !is_activate_all_trigerred && (
          <div className={`fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-100 overflow-hidden bg-white flex flex-col items-center justify-center ${is_landing_logic_not_complete === true ? 'opacity-100' : 'opacity-50'
            }`}
          >
            <Loader size={SIZE.MD} loading />
          </div>
        )}
        <Switch>
          <Route exact path={consumerActivationPaths.Base}>
            <IntroPage path={consumerActivationPaths.Base} />
          </Route>
          <Route path={consumerActivationPaths.AccountSetup}>
            <AccountSetupPage />
          </Route>
          <Route path={consumerActivationPaths.Plan}>
            <IntroPage path={consumerActivationPaths.Base} />
            <PlanCardFooter />
          </Route>
          <Route path={consumerActivationPaths.SimSelection}>
            <SimSelectionPage />
          </Route>
          <Route path={consumerActivationPaths.ChooseNumber}>
            <NewPhoneNumberPage />
          </Route>
          <Route path={consumerActivationPaths.Plans}>
            <PlansPage />
            {(preSelectedPlan || !!currentPlan?.sluggedName) && <PlanCardFooter />}
          </Route>
          <Route path={consumerActivationPaths.Payment}>
            <PaymentPage />
          </Route>
          <Route path={consumerActivationPaths.ProcessLoader}>
            <ProcessLoader />
          </Route>
          <Route path={consumerActivationPaths.ProcessFailure}>
            <ProcessFailure />
          </Route>
          <Route path={consumerActivationPaths.TransactionSummary}>
            <TransactionSummaryPage />
          </Route>
          <Route path={consumerActivationPaths.AlreadyActivated}>
            <AlreadyActivated />
          </Route>
          <Route path={consumerActivationPaths.Verify}>
            <VerifyPage />
          </Route>
          <Route path={consumerActivationPaths.Splash}>
            <ErrorSection mfeError={location.state?.error || null} />
          </Route>
          <Route path="*">
            <ErrorSection errorNum={404} />
          </Route>
        </Switch>
      </ErrorBoundary>
    </>
  )
}

export default ActivationMount
